import { GAME } from '../config';

/**
 * HBullet — player bullet for horizontal mode.
 * Travels right (vx > 0, vy = 0).
 */
export class HBullet {
  x: number;
  y: number;
  vx: number;
  vy: number;
  damage: number;
  alive: boolean = true;
  /**
   * Roguelike「巨彈」upgrade: per-shot size multiplier. 1 = base size.
   * The scene scales the bullet sprite by this factor AND the collision box
   * uses it so a bigger bullet really is easier to land (not just cosmetic).
   * Defaults to 1 so every existing call site / test is unaffected.
   */
  sizeMult: number = 1;
  /**
   * Roguelike「彈跳彈」質變:remaining bounces this bullet may make before it
   * dies on hit. 0 = no bounce (default), so every existing call site / test is
   * unaffected. The scene seeds this from RunModifiers.bounceCount at spawn and
   * decrements it each time the bullet is redirected on hit.
   */
  bouncesLeft: number = 0;
  /**
   * 場內武器進化「貫穿彈」(R-004 包 C):remaining ENEMIES this bullet may pierce
   * THROUGH before it dies on hit. 0 = no pierce (default), so every existing
   * call site / test is unaffected. The scene seeds this from
   * RunModifiers.pierceCount at spawn and decrements it each time the bullet
   * passes through a killed/hit enemy instead of dying. Pierce takes priority
   * over bounce (穿透一整排 vs 反彈) — see _checkCollisions.
   */
  pierceLeft: number = 0;
  /**
   * 穿透傷害衰減計數器(boss 2026-06-14「不能打得又廣傷害又高」):this bullet has
   * already pierced THROUGH this many enemies. The scene reads it to apply
   * geometric damage falloff (pierceDamageFalloff) so the 2nd/3rd pierced enemy
   * takes progressively less than the first — 「廣」不再等於「全場滿傷」。
   * 0 = first hit at full damage. Incremented each time the bullet pierces.
   */
  pierceHits: number = 0;
  /**
   * 場內武器進化「爆裂散彈」(R-004 包 C):per-bullet explosion radius (px). When
   * >0, this bullet triggers an AoE blast on hit even if the run-wide
   * explodeOnHitRadius is 0. Seeded from RunModifiers.spreadBlastRadius on the
   * evolved spread shots. 0 = no per-bullet blast (default).
   */
  blastRadius: number = 0;

  constructor(
    position: { x: number; y: number },
    velocity: { vx: number; vy: number } = { vx: 500, vy: 0 },
    options: {
      damage?: number;
      sizeMult?: number;
      bouncesLeft?: number;
      pierceLeft?: number;
      blastRadius?: number;
    } = {},
  ) {
    this.x = position.x;
    this.y = position.y;
    this.vx = velocity.vx;
    this.vy = velocity.vy;
    this.damage = options.damage ?? 10;
    this.sizeMult = options.sizeMult ?? 1;
    this.bouncesLeft = options.bouncesLeft ?? 0;
    this.pierceLeft = options.pierceLeft ?? 0;
    this.blastRadius = options.blastRadius ?? 0;
  }

  update(dtSec: number): void {
    if (!this.alive) return;
    this.x += this.vx * dtSec;
    this.y += this.vy * dtSec;
    // Remove when it exits the screen. Right edge is the normal case; a
    // 彈跳彈 (bounce) bullet can be redirected toward any edge, so we also cull
    // off the left/top/bottom once it's well outside the play area. Normal
    // straight bullets (vy=0, vx>0) never hit these extra edges before the
    // right-exit, so existing behaviour is unchanged.
    if (
      this.x > GAME.width + 32 ||
      this.x < -32 ||
      this.y < -32 ||
      this.y > GAME.height + 32
    ) {
      this.alive = false;
    }
  }

  kill(): void {
    this.alive = false;
  }
}
